// Type definitions and helper functions for IMSForm component
import { Auth } from 'aws-amplify'
import { CognitoUserSession } from 'amazon-cognito-identity-js'
import { buildRequestHeaders } from '../../../../Utils/ServerUtil.js'
import get from 'lodash/get';
import { saveIMSForms } from '../../../../Utils/SubmitUtil.js';
import config from '../../../../config/runtime'
import { useItemById } from '../../../../services/api'
import { logger } from '../../../../config/logger'

export interface IMSFormProps {
    selectedCategory: {
        forms: any
        id: number
        category: string
    }
    evalItem: {
        item_id: string
        jobcode?: string
        item?: {
            ims_forms_data?: any
            initial_eval?: string
            capture_estimate: number
            sticker_id?: string
        }
    }
    evaluation?: any
    itemData?: any
}

export interface ImsFormsData {
    id: number
    nomination: string
    jobcode: string
    groups: Group[]
    category?: string
    opportunity_id?: string
    capture_estimate?: number
}

export interface Value {
    id: any
    order: any
    value: any
    component_id: any
    component_response: any
}

export interface ComponentResponse {
    value: string
    photo: string
}

export interface Component {
    id: number
    label: string
    label_display: string
    limit: number
    order: number
    values: {
        id: number
        order: number
        value: string
        component_id: number
        component_response: string // Adjust as necessary
    }[]
    prefix: string
    suffix: string
    datatype: string
    group_id: number
    required: string
    max_value: number
    min_value: number
    char_count: number
    max_length: number
    min_length: number
    helper_text: string
    control_type: string
    photo_capture: number
    component_form: any[]
    barcode_scanning: number
    component_response: string | ComponentResponse // Allowing for both types
    is_auto_populated: boolean
}

export interface Group {
    id: number
    title: string
    components: Component[]
    control_type: string
    component_response: any
    values: any[]
}

export interface Category {
    id: number
    title: string
    groups: Group[]
}

/**
 * Gets field value with fallback for both normal values and photo components
 */
export const getFieldValueWithFallback = (
    fieldValue: any,
    originalComponent: any,
    datatype: string,
    isPhotoCapture: boolean
) => {
    let value = '';
    let photo = '';

    if (isPhotoCapture) {
        const originalResponse = originalComponent?.component_response
        const originalValue =
            typeof originalResponse === 'object' &&
            originalResponse !== null &&
            'value' in originalResponse
                ? (originalResponse as any).value
                : originalResponse

        value = fieldValue?.value ?? originalValue

        photo =
            fieldValue?.photo ?? originalComponent?.component_response?.photo

        const normalizedValue = value ?? ''
        return {
            value: convertToDataType(normalizedValue, datatype),
            photo: photo || '',
        }
    } else {
        // First try direct value, then fallback to previous saved value
        const originalResponse = originalComponent?.component_response
        const originalValue =
            typeof originalResponse === 'object' &&
            originalResponse !== null &&
            'value' in originalResponse
                ? (originalResponse as any).value
                : originalResponse

        value =
            (typeof fieldValue === 'object' ? fieldValue?.value : fieldValue) ??
            originalValue

        return convertToDataType(value, datatype)
    }
};

/**
 * Converts values to the appropriate data type
 */
export const convertToDataType = (value: string | boolean, datatype: any) => {
    switch (datatype) {
        case 'boolean':
            return value === true || value === 'true' // Handle string 'true' or boolean true
        case 'number':
            return value === '' || value === null || value === undefined
                ? ''
                : Number(value)
        default:
            return value // Default case: convert to string
    }
}

export const handleIMSFormSave = async (
    saveForm: boolean,
    imsForm: any,
    formData: any,
    ims_forms_data: any,
    fieldValues: { [key: string]: any },
    estimatedHammer: any,
    evalItem: any,
    prevEvalItem: any,
    setSaveForm: (value: boolean) => void,
    handleSaveSuccess: () => void
) => {
    let updatedEvalItem;

    if (!saveForm) return;

    updatedEvalItem = {};
    logger.info('ims_save_start', { item_id: get(evalItem, 'item_id', get(evalItem, 'PK')), ims_id: formData.id })

    if (formData.id) {
        ims_forms_data.groups.forEach((group: any) => {
            group.components.forEach((component: any) => {
                const componentId = String(component.id);
                const fieldValue = fieldValues[componentId];

                const originalComponent = imsForm?.item?.ims_forms_data?.groups
                    ?.flatMap((g: { components: any }) => g.components)
                    ?.find((c: { id: any }) => String(c.id) === componentId);

                const isPhoto = component.photo_capture === 1;

                component.component_response = getFieldValueWithFallback(
                    fieldValue,
                    originalComponent,
                    component.datatype,
                    isPhoto
                );

                // Auto-populate detection
                if (originalComponent?.is_auto_populated) {
                    let originalValue
                    if (isPhoto) {
                        const originalResponse =
                            originalComponent?.component_response
                        originalValue =
                            typeof originalResponse === 'object' &&
                            originalResponse !== null &&
                            'value' in originalResponse
                                ? (originalResponse as any).value
                                : originalResponse
                    } else {
                        originalValue =
                            originalComponent?.component_response?.value ??
                            originalComponent?.component_response
                    }

                    const newValue = isPhoto
                        ? component.component_response?.value
                        : component.component_response
                    component.is_auto_populated = originalValue === newValue
                }

                // Multi-value components (checkbox groups, etc.)
                component.values?.forEach((value: any) => {
                    const valueFieldValue = fieldValues[value.id];
                    if (valueFieldValue !== undefined) {
                        value.component_response = convertToDataType(
                            valueFieldValue?.value !== undefined ? valueFieldValue.value : valueFieldValue,
                            component.datatype
                        );
                    }
                });
            });
        });

        console.log("reached here too...")

        // Sync formData.Categories structure (usually used by UI)
        formData.Categories.forEach((category: { Groups: any[] }) => {
            category.Groups.forEach((group: { Components: any[] }) => {
                group.Components.forEach((component: any) => {
                    const componentId = String(component.id);
                    const fieldValue = fieldValues[componentId];

                    const originalComponent = imsForm?.item?.ims_forms_data?.groups
                        ?.flatMap((g: { components: any }) => g.components)
                        ?.find((c: { id: any }) => String(c.id) === componentId);

                    const isPhoto = component.photo_capture === 1;

                    component.component_response = getFieldValueWithFallback(
                        fieldValue,
                        originalComponent,
                        component.datatype,
                        isPhoto
                    );

                    // Multi-value nested (capital "Values" here)
                    component.Values?.forEach((value: any) => {
                        const val = fieldValues[value.id];
                        if (val !== undefined) {
                            value.component_response = convertToDataType(val, component.datatype);
                        }
                    });
                });
            });
        });

        updatedEvalItem = { ...ims_forms_data };
        if (estimatedHammer !== undefined && estimatedHammer !== null && String(estimatedHammer) !== '') {
            updatedEvalItem.capture_estimate = Number(estimatedHammer);
        }
    } else {
        updatedEvalItem = {};
    }

    const itemId = get(evalItem, 'item_id', get(evalItem, 'PK'));
    if (!itemId) throw new Error('Item ID is missing');

    try {
        const result = await saveIMSForms(itemId, updatedEvalItem, prevEvalItem, setSaveForm, false);

        handleSaveSuccess();
        logger.info('ims_save_success', { item_id: itemId, ims_id: formData.id })
        return updatedEvalItem;
    } catch (e: any) {
        console.error('Error saving IMS form:', e);
        logger.error('ims_save_failure', { item_id: itemId, ims_id: formData.id, message_code: e && e?.message })
    }
};

/**
 * Handles field changes with appropriate type conversions and metadata
 */
export const handleFieldChange = (
    fieldValues: { [key: string]: any },
    groupId: any,
    componentId: string | number,
    newValue: string | boolean,
    datatype: any,
    photoCapture = 0
) => {
    const currentValue = fieldValues[componentId];
    const isEmptyValue =
        newValue === '' ||
        newValue === null ||
        newValue === undefined ||
        (typeof newValue === 'string' && newValue.trim() === '');

    let formattedValue: any;

    if (datatype === 'boolean') {
        formattedValue = {
            value: newValue === true || newValue === 'true',
            is_auto_populated: false,
            was_auto_populated: currentValue?.is_auto_populated || false,
            was_cleared: isEmptyValue
        };
    } else if (photoCapture === 1) {
        formattedValue = {
            value: newValue,
            photo: currentValue?.photo || '',
            is_auto_populated: false,
            was_auto_populated: currentValue?.is_auto_populated || false,
            was_cleared: isEmptyValue
        };
    } else {
        switch (datatype) {
            case 'number':
                formattedValue = {
                    value:
                        newValue === '' || newValue === null || newValue === undefined
                            ? ''
                            : Number(newValue),
                    is_auto_populated: false,
                    was_auto_populated: currentValue?.is_auto_populated || false,
                    was_cleared: isEmptyValue
                };
                break;
            default:
                formattedValue = {
                    value: newValue,
                    is_auto_populated: false,
                    was_auto_populated: currentValue?.is_auto_populated || false,
                    was_cleared: isEmptyValue
                };
        }
    }

    // Create updated field values object
    const updatedFieldValues = { ...fieldValues };
    updatedFieldValues[componentId] = formattedValue;
    return updatedFieldValues;
};

/**
 * Fetches IMS data for an item
 */
// DEPRECATED: Use useItemById hook instead
// This function is kept for backward compatibility
export const fetchIMSData = async (itemId: string) => {
    console.warn('fetchIMSData is deprecated. Use useItemById hook instead.');
    // console.log("In fetchIMSData or fetchIMSDataHelper", {itemId})

    let jwtToken;
    try {
        const session: CognitoUserSession = await Auth.currentSession();
        jwtToken = session.getIdToken().getJwtToken();
    } catch (e) {
        try {
            const session: CognitoUserSession = await Auth.currentSession();
            jwtToken = session.getIdToken().getJwtToken();
        } catch (e) {
            console.error('Error refreshing token', e);
            return null;
        }
    }

    // Use the shared helper function
    const headers = buildRequestHeaders(jwtToken);
    const options: RequestInit = {
        method: 'GET',
        headers,
    };

    const url = `${config.API_ENDPOINT}/items/${itemId}`;

    try {
        const response = await fetch(url, options);
        if (response.ok) {
            const itemData = await response.json();
            return itemData;
        } else {
            console.log('Request failed with status:', response.status);
            return null;
        }
    } catch (error) {
        console.error('Error:', error);
        return null;
    }
};

/**
 * React Query hook for fetching IMS data
 * Use this instead of the deprecated fetchIMSData function
 */
export { useItemById as useFetchIMSData };
